import type {
  CurrencyCode,
  DecimalValue,
  IsoDateTime,
  PortfolioBookInitializationStateV2,
  PortfolioBookKindV2,
  PortfolioBookStatus,
  PortfolioScopeV2,
  PortfolioV2SchemaVersion,
} from "./primitives.js";
import type {
  PortfolioPolicyAssetAllocationV2,
  PortfolioPolicyAssetClassSupportV2,
  PortfolioPolicyBenchmarkPolicyV2,
  PortfolioPolicyExitConstraintsV2,
  PortfolioPolicyHorizonPolicyV2,
  PortfolioPolicyPlannedCapitalFlowV2,
  PortfolioPolicyReviewGovernanceV2,
  PortfolioPolicyRevocationV2,
  PortfolioPolicySourceCostSnapshotV2,
} from "./policy-definition.js";

export interface PortfolioWorkspaceV2 {
  schemaVersion: PortfolioV2SchemaVersion;
  workspaceId: string;
  projectId: string;
  ownerId: string;
  name: string;
  primaryBookId: string | null;
  status: "ACTIVE" | "ARCHIVED";
  createdAt: IsoDateTime;
}

export interface CapitalBookV2 extends PortfolioScopeV2 {
  schemaVersion: PortfolioV2SchemaVersion;
  kind: PortfolioBookKindV2;
  baseCurrency: CurrencyCode;
  capitalNature: "VIRTUAL" | "OWNER_FUNDS";
  executionAuthority: "NONE";
  sourceMode: "MANUAL" | "STATEMENT_IMPORT" | "SIMULATOR_IMPORT";
  status: PortfolioBookStatus;
  initializationState: PortfolioBookInitializationStateV2;
  cashBalance: DecimalValue | null;
  equitySnapshot: { value: DecimalValue; asOf: IsoDateTime; sourceId: string } | null;
  version: number;
  createdAt: IsoDateTime;
  activatedAt: IsoDateTime | null;
}

export interface ProvisionCapitalBookInputV2 {
  workspaceId: string;
  workspaceName: string;
  bookId: string;
  kind: PortfolioBookKindV2;
  baseCurrency: CurrencyCode;
  sourceMode: CapitalBookV2["sourceMode"];
  initialCashBalance: DecimalValue;
}

export const PORTFOLIO_POLICY_STATES_V2 = ["DRAFT", "REVIEWED", "ACTIVE", "SUPERSEDED", "REVOKED"] as const;
export type PortfolioPolicyState = typeof PORTFOLIO_POLICY_STATES_V2[number];
export const PORTFOLIO_CAPITAL_BASIS_V2 = ["OWNER_DECLARED_CAPITAL", "CURRENT_EQUITY"] as const;

/** Evidence captured when a CURRENT_EQUITY policy version is activated. */
export interface PortfolioPolicyCapitalBaseSnapshotV2 {
  value: DecimalValue;
  asOf: IsoDateTime;
  sourceId: string;
}

export interface PortfolioPolicyVersionV2 {
  definitionVersion: "portfolio-policy-definition.v2.2";
  schemaVersion: PortfolioV2SchemaVersion;
  policyVersionId: string;
  bookId: string;
  version: number;
  parentVersionId: string | null;
  state: PortfolioPolicyState;
  baseCurrency: CurrencyCode;
  capitalBasis: typeof PORTFOLIO_CAPITAL_BASIS_V2[number];
  /** Present only for OWNER_DECLARED_CAPITAL. */
  declaredCapital: DecimalValue | null;
  plannedCapitalFlows: PortfolioPolicyPlannedCapitalFlowV2[];
  /** Present only for CURRENT_EQUITY and immutable with the policy version. */
  capitalBaseSnapshot: PortfolioPolicyCapitalBaseSnapshotV2 | null;
  maxOpenRiskPct: DecimalValue;
  maxTradeRiskPct: DecimalValue;
  /** Maximum exposure/concentration of one position. */
  maxPositionPct: DecimalValue;
  maxSectorExposurePct: DecimalValue;
  maxCorrelatedExposurePct: DecimalValue;
  maxPortfolioDrawdownPct: DecimalValue;
  minimumCashReservePct: DecimalValue;
  minimumRewardToRisk: DecimalValue;
  slippageAllowancePerUnit: DecimalValue;
  allowFractionalQuantity: boolean;
  horizonPolicy: PortfolioPolicyHorizonPolicyV2;
  assetAllocationPolicy: PortfolioPolicyAssetAllocationV2;
  exitConstraints: PortfolioPolicyExitConstraintsV2;
  assetClassSupportSnapshot: PortfolioPolicyAssetClassSupportV2[];
  benchmarkPolicy: PortfolioPolicyBenchmarkPolicyV2;
  sourceCostSnapshot: PortfolioPolicySourceCostSnapshotV2;
  reviewGovernance: PortfolioPolicyReviewGovernanceV2;
  revocation: PortfolioPolicyRevocationV2 | null;
  ownerDecisionId: string;
  effectiveAt: IsoDateTime;
  createdAt: IsoDateTime;
}

/** Explicit read-only shape for P2 history; it is never a v2.2 Active policy. */
export type LegacyPortfolioPolicyDraftV2 = Pick<PortfolioPolicyVersionV2,
  "schemaVersion" | "policyVersionId" | "bookId" | "version" | "parentVersionId" | "state"
  | "baseCurrency" | "capitalBasis" | "declaredCapital" | "capitalBaseSnapshot"
  | "maxOpenRiskPct" | "maxTradeRiskPct" | "maxPositionPct" | "minimumCashReservePct"
  | "minimumRewardToRisk" | "slippageAllowancePerUnit" | "allowFractionalQuantity"
  | "ownerDecisionId" | "effectiveAt" | "createdAt"
> & { state: "DRAFT"; legacyDefinitionVersion: "portfolio-policy.v2.1" };

export const PORTFOLIO_POLICY_ISSUE_LEVELS_V2 = ["BLOCK", "REJECT", "WARNING", "OBSERVATION"] as const;
export type PortfolioPolicyIssueLevel = typeof PORTFOLIO_POLICY_ISSUE_LEVELS_V2[number];
export const PORTFOLIO_POLICY_EVALUATION_STATES_V2 = ["VALID", "INVALID"] as const;

export const PORTFOLIO_POLICY_ISSUE_CODES_V2 = [
  "POLICY_STATE_INVALID",
  "INVALID_CURRENCY",
  "INVALID_DECIMAL",
  "PERCENTAGE_OUT_OF_RANGE",
  "NEGATIVE_POLICY_VALUE",
  "DECLARED_CAPITAL_REQUIRED",
  "POLICY_CAPITAL_BASIS_INVALID",
  "CURRENT_EQUITY_SNAPSHOT_REQUIRED",
  "TRADE_RISK_EXCEEDS_OPEN_RISK",
  "OPEN_RISK_DOLLAR_PREVIEW",
] as const;

export type PortfolioPolicyIssueCodeV2 = typeof PORTFOLIO_POLICY_ISSUE_CODES_V2[number];

export interface PortfolioPolicyIssueV2 {
  code: PortfolioPolicyIssueCodeV2;
  copyKey: PortfolioPolicyIssueCodeV2;
  level: PortfolioPolicyIssueLevel;
  field: string;
  inputValue: DecimalValue | string | null;
  allowedValue: DecimalValue | string | null;
}

export interface PortfolioPolicyEvaluationV2 {
  status: typeof PORTFOLIO_POLICY_EVALUATION_STATES_V2[number];
  issues: PortfolioPolicyIssueV2[];
  evaluatedAt: IsoDateTime;
  formulaVersion: "portfolio-policy.v2.1";
}
